import { Link } from "react-router-dom";
import logo from "@/assets/logo-white.png";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { PageTransition } from "@/components/PageTransition";

const PrivacyPolicy = () => {
  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Content */}
        <main className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="prose prose-invert mx-auto max-w-3xl">
              <h1 className="mb-2 text-4xl font-bold">Privacy Policy</h1>
              <p className="mb-8 text-muted-foreground">Last updated: January 2026</p>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">1. Introduction</h2>
                <p className="leading-relaxed text-muted-foreground">
                  PROduce ("we", "our", or "us") is committed to protecting your privacy. This
                  Privacy Policy explains how we collect, use, disclose, and safeguard your
                  information when you use our festival production management platform and mobile
                  application.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">2. Information We Collect</h2>

                <h3 className="mb-2 mt-6 text-lg font-medium">Account Information</h3>
                <p className="leading-relaxed text-muted-foreground">
                  When you create an account, we collect your email address, name, and optionally
                  your phone number and company name.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">Production Data</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We store the production data you create, including briefings, schedules, crew
                  assignments, lineup information, and related content.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">Location Data</h3>
                <p className="leading-relaxed text-muted-foreground">
                  If you enable GPS tracking, we collect location data to facilitate crew
                  coordination during productions. This feature is optional and requires explicit
                  consent. Location data is only collected during active productions and within
                  defined festival boundaries.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">Device Information</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We collect device information including device type, operating system, and app
                  version to provide and improve our services.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">Usage Analytics</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We collect anonymized usage data to understand how our platform is used and to
                  improve our services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">3. How We Use Your Information</h2>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>To provide and maintain our services</li>
                  <li>To manage your account and authentication</li>
                  <li>To enable crew coordination and scheduling features</li>
                  <li>To send push notifications about schedule changes and assignments</li>
                  <li>
                    To facilitate real-time location tracking during productions (when enabled)
                  </li>
                  <li>To improve and optimize our platform</li>
                  <li>To communicate with you about updates and support</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">4. Data Sharing</h2>

                <h3 className="mb-2 mt-6 text-lg font-medium">With Producers</h3>
                <p className="leading-relaxed text-muted-foreground">
                  Crew members' schedule data and location information (when tracking is enabled)
                  are shared with the production's producer to facilitate coordination.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">With crew members</h3>
                <p className="leading-relaxed text-muted-foreground">
                  Producers can choose to share certain information with crew members, such as
                  briefing details, schedules, and optionally the locations of other crew members on
                  the festival map.
                </p>

                <h3 className="mb-2 mt-6 text-lg font-medium">Third Parties</h3>
                <p className="leading-relaxed text-muted-foreground">
                  We do not sell your personal data to third parties. We may share data with service
                  providers who help us operate our platform (hosting, email delivery, payment
                  processing), but only as necessary to provide our services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">5. Data Security</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We implement industry-standard security measures to protect your data, including:
                </p>
                <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>Encryption in transit (HTTPS/TLS)</li>
                  <li>Encryption at rest for sensitive data</li>
                  <li>Secure authentication with password hashing</li>
                  <li>Regular security audits and updates</li>
                  <li>Secure cloud infrastructure</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">6. Your Rights</h2>
                <p className="leading-relaxed text-muted-foreground">You have the right to:</p>
                <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    <strong>Access</strong> – Request a copy of your personal data
                  </li>
                  <li>
                    <strong>Correction</strong> – Request correction of inaccurate data
                  </li>
                  <li>
                    <strong>Deletion</strong> – Request deletion of your account and data
                  </li>
                  <li>
                    <strong>Opt-out</strong> – Disable location tracking at any time in app settings
                  </li>
                  <li>
                    <strong>Data portability</strong> – Export your production data
                  </li>
                </ul>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  To exercise these rights, please contact us at{" "}
                  <a href="mailto:privacy@produce-app.com" className="text-primary hover:underline">
                    privacy@produce-app.com
                  </a>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">7. Cookies</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We use essential cookies to maintain your session and preferences. We also use
                  analytics cookies to understand how our platform is used. You can manage cookie
                  preferences through our cookie banner when you first visit our site.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">8. GPS Tracking</h2>
                <p className="leading-relaxed text-muted-foreground">
                  Our GPS tracking feature is designed with privacy in mind:
                </p>
                <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>Tracking is only active during production events</li>
                  <li>Location is only collected within festival geofence boundaries</li>
                  <li>You can disable tracking at any time in your settings</li>
                  <li>
                    Location data is not retained indefinitely and is deleted after the production
                    ends
                  </li>
                  <li>Producers can only see locations of crew members in their own productions</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">9. Data Retention</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We retain your account data for as long as your account is active. Production data
                  is retained for 12 months after the production date, after which it may be
                  archived or deleted. You can request earlier deletion at any time.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">10. Contact Information</h2>
                <p className="leading-relaxed text-muted-foreground">
                  If you have questions about this Privacy Policy or our data practices, please
                  contact us:
                </p>
                <ul className="mt-4 list-none space-y-1 text-muted-foreground">
                  <li>
                    Email:{" "}
                    <a
                      href="mailto:privacy@produce-app.com"
                      className="text-primary hover:underline"
                    >
                      privacy@produce-app.com
                    </a>
                  </li>
                  <li>
                    Contact form:{" "}
                    <Link to="/contact" className="text-primary hover:underline">
                      produce-app.com/contact
                    </Link>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">11. Changes to This Policy</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We may update this Privacy Policy from time to time. We will notify you of any
                  material changes by posting the new Privacy Policy on this page and updating the
                  "Last updated" date. We encourage you to review this Privacy Policy periodically.
                </p>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-12" style={{ backgroundColor: "#050e22" }}>
          <div className="container mx-auto px-6">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <img src={logo} alt="PROduce Logo" className="h-8" />
              <div className="flex items-center gap-6">
                <Link
                  to="/user-cases"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Use Cases
                </Link>
                <Link
                  to="/privacy-policy"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Contact
                </Link>
              </div>
              <p className="text-sm text-muted-foreground">
                © 2025 PROduce. Elevating festival production worldwide.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default PrivacyPolicy;
